import { createContext, useState, useContext, useCallback, useMemo, ReactNode, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AdminViewAsContextType {
  viewAsUser: { id: string; username: string; full_name?: string } | null;
  setViewAsUser: (user: { id: string; username: string; full_name?: string } | null) => void;
  isAdminViewMode: boolean;
  clearViewAsUser: () => void;
  canViewAs: boolean;
}

const AdminViewAsContext = createContext<AdminViewAsContextType | undefined>(undefined);

export const AdminViewAsProvider = ({ children }: { children: ReactNode }) => {
  const [viewAsUser, setViewAsUser] = useState<{ id: string; username: string; full_name?: string } | null>(null);
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [canViewAs, setCanViewAs] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setIsAdmin(false);
      setCanViewAs(false);
      setViewAsUser(null);
      return;
    }

    try {
      // Check if user is admin in profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      // Check if user has admin or stylist role
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'stylist']);

      const hasAdminOrStylistRole = profile?.is_admin || (userRoles && userRoles.length > 0);
      
      // Only admins get admin access
      setIsAdmin(!!profile?.is_admin);
      
      // Both admins and stylists can view as other users
      setCanViewAs(hasAdminOrStylistRole);
      
      // Clear view-as if user is not admin or stylist
      if (!hasAdminOrStylistRole) {
        setViewAsUser(null);
      }
    } catch (error) {
      console.error('Error checking admin/stylist status:', error);
      setIsAdmin(false);
      setCanViewAs(false);
      setViewAsUser(null);
    }
  };

  const handleSetViewAsUser = useCallback((targetUser: { id: string; username: string; full_name?: string } | null) => {
    if (!canViewAs) {
      toast.error("You don't have permission to view as other users");
      return;
    }
    
    setViewAsUser(targetUser);
    
    if (targetUser) {
      toast.success(`Now viewing as ${targetUser.full_name || targetUser.username}`);
    }
  }, [canViewAs]);

  const clearViewAsUser = useCallback(() => {
    setViewAsUser(null);
    toast.success("Returned to your own view");
  }, []);

  const isAdminViewMode = canViewAs && !!viewAsUser;

  const value = useMemo(() => ({
    viewAsUser,
    setViewAsUser: handleSetViewAsUser,
    isAdminViewMode,
    clearViewAsUser,
    canViewAs,
  }), [viewAsUser, handleSetViewAsUser, isAdminViewMode, clearViewAsUser, canViewAs]);

  return (
    <AdminViewAsContext.Provider value={value}>
      {children}
    </AdminViewAsContext.Provider>
  );
};

export const useAdminViewAs = () => {
  const context = useContext(AdminViewAsContext);
  if (context === undefined) {
    throw new Error("useAdminViewAs must be used within an AdminViewAsProvider");
  }
  return context;
};